import { type ReactNode, useEffect, useRef, useState } from "react";
import "./IconSelect.css";

export interface IconSelectOption {
  value: string;
  label: ReactNode;
  icon?: string;
}

export function IconSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: IconSelectOption[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value) ?? options[0];

  return (
    <div className="icon-select" ref={ref}>
      <button
        type="button"
        className="icon-select-trigger"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
      >
        {selected?.icon && (
          <img className="icon-select-img" src={selected.icon} alt="" />
        )}
        <span className="icon-select-text">{selected?.label}</span>
        <svg
          className={`icon-select-chevron ${
            open ? "icon-select-chevron--open" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <ul className="icon-select-dropdown">
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                className={`icon-select-option ${
                  opt.value === value ? "icon-select-option--active" : ""
                }`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.icon && (
                  <img className="icon-select-img" src={opt.icon} alt="" />
                )}
                <span>{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
